import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {HeadingLarge, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import Log, {Errors} from "@khan/logging";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";

import Error from "./error.tsx";
import {profileLessFiles, type Profile} from "./profile-util.ts";

type Props = {
    kaid: string;
    profilePromise: Promise<Profile>;
    profilePath: string;
    isMyPage: boolean;
    onSufficientlyUsable: () => void;
} & WithMarkConversionProps;

type State = {
    loading: boolean;
    showError: boolean;
};

class Projects extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;

    state: State = {
        loading: true,
        showError: false,
    };

    componentDidMount() {
        this.updateLegacyPrograms();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.kaid !== this.props.kaid) {
            this.updateLegacyPrograms();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    updateLegacyPrograms() {
        const {profilePromise, markConversion, onSufficientlyUsable} =
            this.props;
        this._isMounted = true;
        markConversion({id: "logged_in_homepage_visit"});

        const promises: Array<Promise<any>> = [
            profilePromise,
            import("../../profile-package/profile-tabs.tsx").then(
                (m) => m.default,
            ),
            ...profileLessFiles(),
        ];

        Promise.all(promises)
            // @ts-expect-error - TS2345 - Argument of type '([profile, ProfileTabs]: [any, any]) => void' is not assignable to parameter of type '(value: any[]) => void | PromiseLike<void>'.
            .then(([profile, ProfileTabs]: [any, any]) => {
                if (!this._isMounted) {
                    return;
                }

                this.setState({loading: false});
                ProfileTabs.populatePrograms().then(() => {
                    onSufficientlyUsable();
                });
            })
            .catch((error) => {
                if (this._isMounted) {
                    Log.error(
                        "Error initializing Projects profile:",
                        Errors.Internal,
                        {cause: error},
                    );
                    this.setState({showError: true});
                }
            });
    }

    render(): React.ReactNode {
        const {profilePath, isMyPage} = this.props;
        const {loading, showError} = this.state;

        if (showError) {
            return <Error />;
        }

        return (
            <View style={styles.container}>
                <LabelLarge style={styles.breadcrumb}>
                    <Link href={profilePath}>{t`< Profile`}</Link>
                </LabelLarge>
                <HeadingLarge style={styles.heading}>
                    {isMyPage ? t`My projects` : t`Projects`}
                </HeadingLarge>
                {loading && <RouteLoadSpinner />}
                <View
                    id="tab-content-projects"
                    className="profile-scratchpads-list lihp-projects"
                    style={styles.scratchpadsList}
                />
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 32,
    },
    breadcrumb: {
        marginBottom: 4,
    },
    heading: {
        marginBottom: 22,
    },
    scratchpadsList: {
        marginTop: -54,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof Projects,
        React.ComponentProps<typeof Projects>
    >
>;
const ProjectsWithLearningTime: React.ComponentType<ExportProps> =
    withMarkConversion(Projects);
export default ProjectsWithLearningTime;
